/**
 * Simple drag-and-drop email block builder.
 *
 * Blocks compile to HTML on save. Storage on the server is:
 *  • blocks_json: JSON string of the block array (source of truth for the editor)
 *  • body_html:   compiled HTML (what actually gets sent)
 *
 * Reordering uses native HTML5 drag-and-drop — no extra dependency.
 *
 * Block types:
 *  - heading:   { type, text, level: 1|2|3 }
 *  - paragraph: { type, text }
 *  - button:    { type, text, url }
 *  - image:     { type, src, alt }
 *  - divider:   { type }
 *  - spacer:    { type, height: number (px) }
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  GripVertical, Trash2, Heading as HeadingIcon, Type, Link2, Image as ImageIcon,
  Minus, ChevronsUpDown, Plus,
} from "lucide-react";

const uid = () => Math.random().toString(36).slice(2, 10);

const BLOCK_META = {
  heading:   { label: "عنوان",  Icon: HeadingIcon },
  paragraph: { label: "فقرة",   Icon: Type },
  button:    { label: "زر",     Icon: Link2 },
  image:     { label: "صورة",   Icon: ImageIcon },
  divider:   { label: "فاصل",   Icon: Minus },
  spacer:    { label: "مسافة",  Icon: ChevronsUpDown },
};

const defaultBlock = (type) => {
  const id = uid();
  switch (type) {
    case "heading":   return { id, type, text: "عنوان جذاب", level: 2 };
    case "paragraph": return { id, type, text: "اكتب هنا نص الفقرة..." };
    case "button":    return { id, type, text: "اضغط هنا", url: "https://" };
    case "image":     return { id, type, src: "", alt: "" };
    case "divider":   return { id, type };
    case "spacer":    return { id, type, height: 24 };
    default:          return { id, type: "paragraph", text: "" };
  }
};

// Escape user text for safe HTML embedding.
const esc = (s = "") => String(s)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;").replace(/'/g, "&#39;");

// Preserve template variables like {{full_name}} — un-escape braces after esc()
const escKeepVars = (s = "") => esc(s).replace(/\{\{\s*([\w.]+)\s*\}\}/g, "{{$1}}");

// Turn markdown-style `[text](url)` into a real anchor tag AFTER escaping.
// Because the text was already HTML-escaped by escKeepVars(), the brackets
// come through as `[…]&#40;…&#41;` -- but parentheses aren't escaped by esc(),
// so we can still find the `(url)` cleanly. We match on the pre-escape text
// first, then re-escape only the text/url pieces.
const compileParagraphText = (raw = "") => {
  // Split into runs: link vs plain. We walk the RAW string and emit escaped
  // pieces + anchor tags. Placeholder-friendly: `{{checkout_link}}` inside
  // the URL survives untouched.
  const linkRe = /\[([^\]\n]+?)\]\(([^)\n\s]+)\)/g;
  let out = "";
  let last = 0;
  let m;
  while ((m = linkRe.exec(raw)) !== null) {
    out += escKeepVars(raw.slice(last, m.index));
    const label = escKeepVars(m[1]);
    // Allow `{{var}}` in the URL (do NOT html-escape braces); still escape
    // quotes/angle-brackets to keep the href attribute safe.
    const url = m[2]
      .replace(/&/g, "&amp;").replace(/"/g, "&quot;")
      .replace(/</g, "&lt;").replace(/>/g, "&gt;");
    out += `<a href="${url}" style="color:#FF6A00;text-decoration:underline;font-weight:600;">${label}</a>`;
    last = m.index + m[0].length;
  }
  out += escKeepVars(raw.slice(last));
  return out.replace(/\n/g, "<br/>");
};

export function compileBlocksToHtml(blocks) {
  return blocks.map((b) => {
    switch (b.type) {
      case "heading": {
        const lvl = Math.min(3, Math.max(1, parseInt(b.level) || 2));
        const sizes = { 1: "26px", 2: "22px", 3: "18px" };
        return `<h${lvl} style="margin:0 0 12px;font-size:${sizes[lvl]};line-height:1.35;color:#1f1f1f;">${escKeepVars(b.text)}</h${lvl}>`;
      }
      case "paragraph":
        return `<p style="margin:0 0 12px;font-size:15px;line-height:1.7;color:#333;">${compileParagraphText(b.text)}</p>`;
      case "button":
        return `<div style="text-align:center;margin:16px 0;"><a href="${esc(b.url || "#")}" style="display:inline-block;padding:12px 28px;background:#31614F;color:#fff;text-decoration:none;border-radius:999px;font-weight:600;font-size:15px;">${escKeepVars(b.text)}</a></div>`;
      case "image":
        if (!b.src) return "";
        return `<div style="text-align:center;margin:12px 0;"><img src="${esc(b.src)}" alt="${esc(b.alt || "")}" style="max-width:100%;height:auto;border-radius:8px;" /></div>`;
      case "divider":
        return `<hr style="border:0;border-top:1px solid #e5e5e5;margin:20px 0;" />`;
      case "spacer": {
        const h = Math.max(4, Math.min(160, parseInt(b.height) || 24));
        return `<div style="height:${h}px;line-height:${h}px;">&nbsp;</div>`;
      }
      default:
        return "";
    }
  }).join("\n");
}

// Paragraph block editor with a small toolbar for inserting hyperlinks.
// Users can either type markdown-style `[label](url)` directly, or click
// "أدرج رابط" to insert a template at the caret with the label pre-selected.
function ParagraphBlockEditor({ block, onUpdate }) {
  const taRef = useRef(null);

  const insertLink = () => {
    const ta = taRef.current;
    if (!ta) return;
    const label = "نص الرابط";
    const template = `[${label}](https://)`;
    const start = ta.selectionStart ?? ta.value.length;
    const end = ta.selectionEnd ?? ta.value.length;
    // If the user has selected some text, wrap it as the link label.
    const selected = ta.value.slice(start, end);
    const insertLabel = selected || label;
    const insertText = `[${insertLabel}](https://)`;
    const next = ta.value.slice(0, start) + insertText + ta.value.slice(end);
    onUpdate({ text: next });
    // After React re-renders, select the URL portion so the user can type it.
    const urlStart = start + insertLabel.length + 3;   // past "[label]("
    const urlEnd = urlStart + "https://".length;
    requestAnimationFrame(() => {
      if (!taRef.current) return;
      taRef.current.focus();
      taRef.current.setSelectionRange(urlStart, urlEnd);
    });
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={insertLink}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] bg-white hover:bg-primary/10 border border-foreground/10 transition-colors"
          data-testid={`block-para-insert-link-${block.id}`}
          title="حدّد النص أولاً ثم اضغط لتحويله لرابط، أو اضغط لإدراج قالب رابط جديد"
        >
          <Link2 className="h-3 w-3" /> أدرج رابط
        </button>
        <span className="text-[10px] text-foreground/50" dir="ltr">
          [نص الرابط](https://…) ← تنسيق الرابط
        </span>
      </div>
      <Textarea
        ref={taRef}
        rows={3}
        value={block.text}
        onChange={(e) => onUpdate({ text: e.target.value })}
        placeholder="نص الفقرة — استخدم [نص]( رابط ) لإضافة رابط"
        data-testid={`block-para-text-${block.id}`}
      />
    </div>
  );
}

export default function EmailBlockBuilder({ blocks, onChange }) {
  const [dragId, setDragId] = useState(null);
  // Only arm HTML5 drag on the block whose grip handle is being pressed.
  // Without this, having `draggable` on the whole card steals mousedown from
  // inputs/textareas inside → clicking mid-text can't reposition the cursor.
  const [armedId, setArmedId] = useState(null);

  const setBlocks = (updater) => {
    const next = typeof updater === "function" ? updater(blocks) : updater;
    onChange(next);
  };

  const addBlock = (type) => setBlocks((b) => [...b, defaultBlock(type)]);
  const removeBlock = (id) => setBlocks((b) => b.filter((x) => x.id !== id));
  const updateBlock = (id, patch) => setBlocks((b) => b.map((x) => x.id === id ? { ...x, ...patch } : x));

  // HTML5 drag/drop reorder — no library.
  const onDragStart = (id) => (e) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (targetId) => (e) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) return;
    setBlocks((b) => {
      const from = b.findIndex((x) => x.id === dragId);
      const to = b.findIndex((x) => x.id === targetId);
      if (from < 0 || to < 0) return b;
      const next = [...b];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };
  const onDragEnd = () => { setDragId(null); setArmedId(null); };

  return (
    <div className="space-y-3" data-testid="email-block-builder">
      {/* Add block toolbar */}
      <div className="flex flex-wrap items-center gap-1.5 p-2 bg-foreground/5 rounded-lg" data-testid="block-toolbar">
        <span className="text-[11px] text-foreground/60 me-1"><Plus className="h-3 w-3 inline" /> إضافة عنصر:</span>
        {Object.entries(BLOCK_META).map(([type, meta]) => (
          <button
            key={type}
            type="button"
            onClick={() => addBlock(type)}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs bg-white hover:bg-primary/10 border border-foreground/10 transition-colors"
            data-testid={`block-add-${type}`}
          >
            <meta.Icon className="h-3 w-3" />
            <span>{meta.label}</span>
          </button>
        ))}
      </div>

      {/* Block list */}
      {blocks.length === 0 && (
        <div className="text-center text-sm text-foreground/50 py-8 border-2 border-dashed rounded-lg" data-testid="blocks-empty">
          لا توجد عناصر بعد. أضف من الأزرار أعلاه لتبدأ ببناء بريدك.
        </div>
      )}

      <div className="space-y-2">
        {blocks.map((b, idx) => {
          const meta = BLOCK_META[b.type];
          const dragging = dragId === b.id;
          return (
            <div
              key={b.id}
              draggable={armedId === b.id}
              onDragStart={onDragStart(b.id)}
              onDragOver={onDragOver(b.id)}
              onDragEnd={onDragEnd}
              onDrop={onDragEnd}
              className={`bg-white rounded-lg border transition-shadow ${dragging ? "opacity-40 shadow-lg" : "shadow-sm hover:shadow"}`}
              data-testid={`block-${b.id}`}
            >
              <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/30 rounded-t-lg">
                <button
                  type="button"
                  onMouseDown={() => setArmedId(b.id)}
                  onMouseUp={() => setArmedId(null)}
                  onTouchStart={() => setArmedId(b.id)}
                  onTouchEnd={() => setArmedId(null)}
                  className="cursor-grab active:cursor-grabbing text-foreground/40 hover:text-foreground touch-none"
                  data-testid={`block-drag-${b.id}`}
                  title="اسحب لإعادة الترتيب"
                >
                  <GripVertical className="h-4 w-4" />
                </button>
                <meta.Icon className="h-3.5 w-3.5 text-foreground/60" />
                <span className="text-xs font-medium">{meta.label}</span>
                <span className="text-[10px] text-foreground/40">#{idx + 1}</span>
                <div className="flex-1" />
                <button
                  type="button"
                  onClick={() => removeBlock(b.id)}
                  className="text-red-600 hover:bg-red-50 rounded p-1"
                  data-testid={`block-remove-${b.id}`}
                  title="حذف"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="p-3 space-y-2">
                {b.type === "heading" && (
                  <>
                    <Input value={b.text} onChange={(e) => updateBlock(b.id, { text: e.target.value })} placeholder="نص العنوان" data-testid={`block-heading-text-${b.id}`} />
                    <div className="flex items-center gap-2 text-xs">
                      <Label className="text-xs">حجم</Label>
                      <Select value={String(b.level || 2)} onValueChange={(v) => updateBlock(b.id, { level: parseInt(v) })}>
                        <SelectTrigger className="h-8 w-28"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">كبير</SelectItem>
                          <SelectItem value="2">متوسط</SelectItem>
                          <SelectItem value="3">صغير</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
                {b.type === "paragraph" && (
                  <ParagraphBlockEditor block={b} onUpdate={(patch) => updateBlock(b.id, patch)} />
                )}
                {b.type === "button" && (
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div>
                      <Label className="text-xs">نص الزر</Label>
                      <Input value={b.text} onChange={(e) => updateBlock(b.id, { text: e.target.value })} data-testid={`block-button-text-${b.id}`} />
                    </div>
                    <div>
                      <Label className="text-xs">الرابط</Label>
                      <Input value={b.url} onChange={(e) => updateBlock(b.id, { url: e.target.value })} placeholder="https://..." dir="ltr" data-testid={`block-button-url-${b.id}`} />
                    </div>
                  </div>
                )}
                {b.type === "image" && (
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs">رابط الصورة</Label>
                      <Input value={b.src} onChange={(e) => updateBlock(b.id, { src: e.target.value })} placeholder="https://..." dir="ltr" data-testid={`block-image-src-${b.id}`} />
                    </div>
                    <div>
                      <Label className="text-xs">نص بديل (Alt)</Label>
                      <Input value={b.alt} onChange={(e) => updateBlock(b.id, { alt: e.target.value })} data-testid={`block-image-alt-${b.id}`} />
                    </div>
                  </div>
                )}
                {b.type === "divider" && (
                  <p className="text-xs text-foreground/50">— خط فاصل أفقي —</p>
                )}
                {b.type === "spacer" && (
                  <div className="flex items-center gap-2">
                    <Label className="text-xs">ارتفاع (بكسل)</Label>
                    <Input
                      type="number" min={4} max={160} value={b.height}
                      onChange={(e) => updateBlock(b.id, { height: parseInt(e.target.value) || 24 })}
                      className="w-24"
                      data-testid={`block-spacer-height-${b.id}`}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
